import { useEffect, useState } from 'react';
import { Heading, Text, Button, TextField, TextArea, Badge } from 'frosted-ui';
import { Lock16, Plus16, Trash16 } from '@frosted-ui/icons';
import { toMinorUnits, formatCurrency } from '../../utils/currency';
import { variantLabel } from '../../utils/variants';
import {
  UNLOCK_CONTENT_TYPES,
  UNLOCK_CURRENCY,
  UNLOCK_MAX_FILE_BYTES,
  unlockDraftProblem,
  type UnlockDraft,
} from './unlockDraft';
import ImageFilePicker from './ImageFilePicker';

const NAMES = ['A', 'B', 'C'];

function majorString(minor: number): string {
  return (minor / 100).toFixed(2);
}

interface StepWhatToSellProps {
  draft: UnlockDraft;
  onChange: (updates: Partial<UnlockDraft>) => void;
  /** The draft exists but its image didn't get through: only the image can change. */
  saved?: boolean;
}

/**
 * "What to test" for a pay-to-unlock offer: the content, the copy buyers see,
 * and 2–3 one-time prices to split visitors between.
 */
export default function StepWhatToSell({ draft, onChange, saved = false }: StepWhatToSellProps) {
  const [fileError, setFileError] = useState<string | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [priceInputs, setPriceInputs] = useState<string[]>(() => draft.prices.map(majorString));

  // Local object URL for the picked file; revoked when it changes.
  useEffect(() => {
    if (!draft.file) {
      setPreviewSrc(null);
      return;
    }
    const url = URL.createObjectURL(draft.file);
    setPreviewSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [draft.file]);

  function pickFile(file: File | null) {
    setFileError(null);
    if (file && !UNLOCK_CONTENT_TYPES.includes(file.type)) {
      setFileError('Only JPEG, PNG, or WebP images are supported');
      return;
    }
    if (file && file.size > UNLOCK_MAX_FILE_BYTES) {
      setFileError('Image must be under 10 MB');
      return;
    }
    onChange({ file });
  }

  function setPrices(inputs: string[]) {
    setPriceInputs(inputs);
    onChange({
      prices: inputs.map((value) => {
        const parsed = parseFloat(value);
        return Number.isNaN(parsed) ? 0 : toMinorUnits(parsed, UNLOCK_CURRENCY);
      }),
    });
  }

  const problem = unlockDraftProblem(draft);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div>
        <Heading size="5" style={{ marginBottom: 'var(--space-1)' }}>What to sell</Heading>
        <Text size="2" color="gray">
          Buyers see a blurred preview and unlock the image right on the page after paying. Each
          visitor is shown one of your prices.
        </Text>
        {saved && (
          <Text size="2" style={{ display: 'block', marginTop: 'var(--space-2)' }}>
            This draft is saved with these prices and this title. You can still choose a different
            image. To change anything else, delete the draft and start again.
          </Text>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--space-4)', alignItems: 'start' }}>
        <ImageFilePicker
          label="Image"
          hint="JPEG, PNG, or WebP, up to 10 MB"
          accept={UNLOCK_CONTENT_TYPES}
          file={draft.file}
          error={fileError}
          onPick={pickFile}
        />

        {/* What a visitor sees before paying (the real preview is blurred on our server). */}
        <div
          aria-label="Buyer preview"
          style={{
            position: 'relative',
            borderRadius: 'var(--radius-4)',
            overflow: 'hidden',
            backgroundColor: 'var(--gray-a3)',
            aspectRatio: '4 / 3',
          }}
        >
          {previewSrc && (
            <img
              src={previewSrc}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: 'blur(18px)', transform: 'scale(1.1)' }}
            />
          )}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)',
              background: 'rgba(0, 0, 0, 0.2)',
            }}
          >
            <Lock16 style={{ width: 22, height: 22, color: 'white' }} />
            <Badge color="gray" highContrast>
              {previewSrc ? 'What buyers see before paying' : 'Your image appears here, blurred'}
            </Badge>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        <Text size="2" weight="medium">Title buyers see</Text>
        <TextField.Root>
          <TextField.Input
            placeholder="e.g. Tonight's play"
            value={draft.title}
            maxLength={120}
            disabled={saved}
            onChange={(e) => onChange({ title: e.target.value })}
          />
        </TextField.Root>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        <Text size="2" weight="medium">Teaser (optional)</Text>
        <TextArea
          placeholder="Shown above the blurred preview — sell the unlock"
          value={draft.description}
          maxLength={500}
          disabled={saved}
          onChange={(e) => onChange({ description: e.target.value })}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <Text size="2" weight="medium">Prices to test (one-time, USD)</Text>
        {priceInputs.map((value, i) => (
          <div key={NAMES[i]} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <Text size="2" color="gray" style={{ width: 110 }}>
              {variantLabel(NAMES[i])}
            </Text>
            <div style={{ flex: '1 1 140px', maxWidth: 200 }}>
              <TextField.Root>
                <TextField.Slot>$</TextField.Slot>
                <TextField.Input
                  aria-label={`${variantLabel(NAMES[i])} price`}
                  inputMode="decimal"
                  disabled={saved}
                  value={value}
                  onChange={(e) => setPrices(priceInputs.map((v, j) => (j === i ? e.target.value : v)))}
                />
              </TextField.Root>
            </div>
            {i === 2 && !saved && (
              <Button
                variant="ghost"
                color="gray"
                size="1"
                aria-label="Remove this price"
                onClick={() => setPrices(priceInputs.slice(0, 2))}
              >
                <Trash16 />
              </Button>
            )}
          </div>
        ))}
        {priceInputs.length < 3 && !saved && (
          <div>
            <Button
              variant="soft"
              size="1"
              onClick={() =>
                setPrices([...priceInputs, majorString(Math.max(...draft.prices, 100) + 500)])
              }
            >
              <Plus16 /> Add a third price
            </Button>
          </div>
        )}
        <Text size="1" color="gray">
          Visitors are split evenly. Control is the price you'd charge today
          {draft.prices[0] >= 100 ? ` (${formatCurrency(draft.prices[0], UNLOCK_CURRENCY)})` : ''}.
        </Text>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 180px', display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          <Text size="2" weight="medium">Stops selling (optional)</Text>
          <TextField.Root>
            <TextField.Input
              type="datetime-local"
              disabled={saved}
              value={draft.expiresAt}
              onChange={(e) => onChange({ expiresAt: e.target.value })}
            />
          </TextField.Root>
        </div>
        <div style={{ flex: '1 1 140px', display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          <Text size="2" weight="medium">Max sales (optional)</Text>
          <TextField.Root>
            <TextField.Input
              placeholder="Unlimited"
              inputMode="numeric"
              disabled={saved}
              value={draft.maxSales}
              onChange={(e) => onChange({ maxSales: e.target.value })}
            />
          </TextField.Root>
        </div>
      </div>

      {problem && (
        <Text size="2" color="gray">
          {problem}
        </Text>
      )}
    </div>
  );
}
